import { Box, Flex, Group, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { SCHEDULE_DAY_OPTIONS } from 'constants/menuManagement';
import { breakpoints } from 'styles/theme.variables';

import EditScheduleMenu from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/EditScheduleMenu';
import { ErrorCountBadge } from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/ErrorCountBadge';
import TimeSelect from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/TimeSelect';
import DaySwitch from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/StandardHours/DaySwitch';
import {
  hasRolloverHours,
  isDayClosed,
  getScheduleTimeValue,
  resolvePrimaryId,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';
import {
  ValidationError,
  DayOption,
  TimeOption,
} from 'components/MenuManagement/MenuEdit/Hours/types';
import { getTimeInputError } from 'components/MenuManagement/MenuEdit/Hours/validation';

type Schedule = {
  uuid?: string;
  tempId?: string;
  startTime: string;
  endTime: string;
  dayIndex: string;
  isPeriod?: boolean;
  appliedDate?: string;
};

type StandardHoursInputViewProps = {
  compact?: boolean;
  visibleSchedules: Schedule[];
  timeOptions: TimeOption[];
  dayOptions: DayOption[];
  handleToggle: (id: string) => void;
  handleChange: (val: string, id: string, type: string) => void;
  handle24Hours: (id: string) => void;
  handleDeleteHours: (id: string) => void;
  handleAddPeriod: (id: string) => void;
  getValidationErrors?: (dayIndex: string | number) => ValidationError[];
};

const TIME_SELECT_WIDTH = 202;

const COMPACT_TIME_SELECT_WIDTH = 112;

const COMPACT_TOGGLE_ROW_HEIGHT = 40;

type SchedulesByDay = {
  [key: string]: Schedule[];
};

export default function StandardHoursInputView({
  compact = false,
  visibleSchedules,
  timeOptions,
  dayOptions,
  handleToggle,
  handleChange,
  handle24Hours,
  handleDeleteHours,
  handleAddPeriod,
  getValidationErrors,
}: StandardHoursInputViewProps) {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });

  const timeSelectWidth = compact ? COMPACT_TIME_SELECT_WIDTH : TIME_SELECT_WIDTH;

  const hasCompactMobileRows = isMobile && compact;
  const daysGap = isMobile && !compact ? 'xl' : 'lg';
  const primaryRowGap = hasCompactMobileRows ? 'xs' : 'sm';

  const groupedSchedules = visibleSchedules.reduce<SchedulesByDay>((schedulesByDay, schedule) => {
    const dayKey = schedule.dayIndex.toString();
    return {
      ...schedulesByDay,
      [dayKey]: [...(schedulesByDay[dayKey] || []), schedule],
    };
  }, {});

  const getStartError = (dayIndex: number | string, scheduleId: string) =>
    getTimeInputError(dayIndex.toString(), scheduleId, false, getValidationErrors);

  const getEndError = (dayIndex: number | string, scheduleId: string) =>
    getTimeInputError(dayIndex.toString(), scheduleId, true, getValidationErrors);

  return (
    <Stack gap={daysGap}>
      {SCHEDULE_DAY_OPTIONS.map(dayOption => {
        const daySchedules = groupedSchedules[dayOption.value] || [];
        const primarySchedule = daySchedules.find(schedule => !schedule.isPeriod);
        const secondarySchedules = daySchedules.filter(schedule => schedule.isPeriod);

        if (!primarySchedule) return null;
        const primaryId = resolvePrimaryId(primarySchedule);
        const mappedDayOption = dayOptions.find(
          option => option.value === primarySchedule.dayIndex,
        );

        if (!primaryId || !mappedDayOption) return null;

        const dayErrors = getValidationErrors?.(dayOption.value) ?? [];

        return (
          <Stack gap="xs" key={dayOption.value}>
            {/* Primary hours */}
            <Flex
              direction={isMobile ? 'column' : 'row'}
              gap={isMobile ? primaryRowGap : 'xs'}
              wrap="nowrap"
              maw={isMobile ? undefined : 600}
              align={isMobile ? undefined : 'center'}
            >
              <DaySwitch
                minHeight={compact ? COMPACT_TOGGLE_ROW_HEIGHT : undefined}
                primarySchedule={primarySchedule}
                primaryId={primaryId}
                dayOption={mappedDayOption}
                hasSecondarySchedules={secondarySchedules.length > 0}
                handleToggle={handleToggle}
              />
              <Group gap="xs3" wrap="nowrap">
                <TimeSelect
                  timeOptions={timeOptions}
                  value={getScheduleTimeValue(primarySchedule, timeOptions)}
                  onSelectNewTime={v => handleChange(v, primaryId, 'startTime')}
                  disabled={isDayClosed(primarySchedule)}
                  error={getStartError(dayOption.value, primaryId)}
                  w={isMobile ? undefined : timeSelectWidth}
                  flex={isMobile ? 1 : undefined}
                />
                <Text component="span" mx={isMobile ? 'xs3' : 0}>
                  —
                </Text>
                <TimeSelect
                  timeOptions={timeOptions}
                  value={getScheduleTimeValue(primarySchedule, timeOptions, { isEndTime: true })}
                  onSelectNewTime={v => handleChange(v, primaryId, 'endTime')}
                  disabled={isDayClosed(primarySchedule)}
                  error={getEndError(dayOption.value, primaryId)}
                  w={isMobile ? undefined : timeSelectWidth}
                  flex={isMobile ? 1 : undefined}
                />
                <Group gap="xs3" ml={compact ? 'xs3' : undefined} wrap="nowrap" align="center">
                  {primaryId && (
                    <EditScheduleMenu
                      compact={compact}
                      schedule={primarySchedule}
                      schedules={daySchedules}
                      handle24Hours={() => handle24Hours(primaryId)}
                      handleDeleteHours={() => handleDeleteHours(primaryId)}
                      handleAddPeriod={() => handleAddPeriod(primaryId)}
                    />
                  )}
                  <ErrorCountBadge
                    validationErrors={dayErrors}
                    scheduleId={primaryId}
                    hasRollover={hasRolloverHours(primarySchedule)}
                  />
                </Group>
              </Group>
            </Flex>

            {/* Periods */}
            {secondarySchedules.map(schedule => {
              const scheduleId = resolvePrimaryId(schedule, true);

              if (!scheduleId) return null;

              const periodDayErrors = getValidationErrors?.(dayOption.value) ?? [];

              return (
                <Flex
                  direction={isMobile ? 'column' : 'row'}
                  gap={isMobile ? 'sm' : 'xs'}
                  wrap="nowrap"
                  maw={isMobile ? undefined : 600}
                  align={isMobile ? undefined : 'center'}
                  key={scheduleId}
                >
                  {!isMobile && <Box flex="none" w={98} />}
                  <Group gap="xs3" wrap="nowrap">
                    <TimeSelect
                      timeOptions={timeOptions}
                      value={getScheduleTimeValue(schedule, timeOptions)}
                      onSelectNewTime={v => handleChange(v, scheduleId, 'startTime')}
                      disabled={isDayClosed(schedule)}
                      error={getStartError(dayOption.value, scheduleId)}
                      w={isMobile ? undefined : timeSelectWidth}
                      flex={isMobile ? 1 : undefined}
                    />
                    <Text component="span" mx={isMobile ? 'xs3' : 0}>
                      —
                    </Text>
                    <TimeSelect
                      timeOptions={timeOptions}
                      value={getScheduleTimeValue(schedule, timeOptions, { isEndTime: true })}
                      onSelectNewTime={v => handleChange(v, scheduleId, 'endTime')}
                      disabled={isDayClosed(schedule)}
                      error={getEndError(dayOption.value, scheduleId)}
                      w={isMobile ? undefined : timeSelectWidth}
                      flex={isMobile ? 1 : undefined}
                    />
                    <Group gap="xs3" ml={compact ? 'xs3' : undefined} wrap="nowrap" align="center">
                      <EditScheduleMenu
                        compact={compact}
                        schedule={schedule}
                        schedules={daySchedules}
                        handleDeleteHours={() => handleDeleteHours(scheduleId)}
                      />
                      <ErrorCountBadge
                        validationErrors={periodDayErrors}
                        scheduleId={scheduleId}
                        hasRollover={hasRolloverHours(schedule)}
                      />
                    </Group>
                  </Group>
                </Flex>
              );
            })}
          </Stack>
        );
      })}
    </Stack>
  );
}
